import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchFeatureFlags, updateFeatureFlag } from '../api';
import { setFeatureFlagAdminVisible } from '../state/adminUIVisibility';
import type { FeatureFlag, FeatureFlagState, PluginHealthInfo } from '../types';
import { initializePlugins } from '~/plugins/ClientPluginLoader';
import * as WorkHistoryHookRegistry from '~/plugins/WorkHistoryHookRegistry';

export type UpdateStatus = {
  type: 'success' | 'error';
  message: string;
} | null;

export type FeatureFlagContextValue = {
  flags: FeatureFlag[];
  unlock: boolean;
  pluginHealth?: PluginHealthInfo;
  isLoading: boolean;
  savingFlags: Set<string>;
  isUnlockSaving: boolean;
  updateStatus: UpdateStatus;
  reloadRequired: boolean;
  isEnabled: (flagKey: string) => boolean;
  refresh: () => Promise<void>;
  updateFlag: (flagKey: string, enabled: boolean) => Promise<void>;
  updateUnlock: (enabled: boolean) => Promise<void>;
  clearStatus: () => void;
};

export const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: [],
  unlock: false,
  pluginHealth: undefined,
  isLoading: true,
  savingFlags: new Set(),
  isUnlockSaving: false,
  updateStatus: null,
  reloadRequired: false,
  isEnabled: () => false,
  refresh: async () => {},
  updateFlag: async () => {},
  updateUnlock: async () => {},
  clearStatus: () => {},
});

const normalizeState = (state: FeatureFlagState | null): FeatureFlagState => {
  if (!state) return { unlock: false, flags: [] };
  return {
    unlock: state.unlock ?? false,
    flags: state.flags ?? [],
    adminUI: state.adminUI,
    pluginHealth: state.pluginHealth,
  };
};

const areFlagsEqual = (left: FeatureFlag[], right: FeatureFlag[]): boolean => {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    const leftFlag = left[index];
    const rightFlag = right[index];
    if (
      leftFlag.key !== rightFlag.key ||
      leftFlag.enabled !== rightFlag.enabled ||
      leftFlag.label !== rightFlag.label ||
      leftFlag.source !== rightFlag.source
    ) {
      return false;
    }
  }
  return true;
};

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
};

const arePluginHealthEqual = (
  left?: PluginHealthInfo,
  right?: PluginHealthInfo,
): boolean => {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return (
    areStringArraysEqual(left.registeredKeys, right.registeredKeys) &&
    areStringArraysEqual(left.resolvedKeys, right.resolvedKeys) &&
    areStringArraysEqual(left.unresolvedKeys, right.unresolvedKeys)
  );
};

type FeatureFlagProviderProps = {
  children: React.ReactNode;
  pollingIntervalMs?: number;
};

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
  pollingIntervalMs,
}) => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [unlock, setUnlock] = useState(false);
  const [pluginHealth, setPluginHealth] = useState<PluginHealthInfo | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const pluginsInitializedRef = useRef(false);
  const [pluginsInitialized, setPluginsInitialized] = useState(false);
  const [savingFlags, setSavingFlags] = useState<Set<string>>(new Set());
  const [isUnlockSaving, setIsUnlockSaving] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>(null);
  const [reloadRequired, setReloadRequired] = useState(false);

  const clearStatus = useCallback(() => {
    setUpdateStatus(null);
  }, []);

  const applyState = useCallback((state: FeatureFlagState | null) => {
    const normalized = normalizeState(state);
    setFlags((prev) => (areFlagsEqual(prev, normalized.flags) ? prev : normalized.flags));
    setUnlock((prev) => (prev === normalized.unlock ? prev : normalized.unlock));
    setPluginHealth((prev) =>
      arePluginHealthEqual(prev, normalized.pluginHealth) ? prev : normalized.pluginHealth,
    );
    setFeatureFlagAdminVisible(state?.adminUI?.featureFlags?.visible ?? true);
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    const state = await fetchFeatureFlags();
    applyState(state);
    setIsLoading(false);
  }, [applyState]);

  useEffect(() => {
    let isMounted = true;

    const loadInitialState = async () => {
      let flagsData: import('../types').FeatureFlagState | null = null;
      try {
        const state = await fetchFeatureFlags();
        if (!isMounted) return;
        flagsData = state;
        applyState(state);
      } catch {
        if (!isMounted) return;
        applyState(null);
      } finally {
        if (!isMounted) return;
        if (!pluginsInitializedRef.current) {
          initializePlugins(flagsData?.pluginHierarchy ?? {});
          pluginsInitializedRef.current = true;
        }
        setPluginsInitialized(true);
        setIsLoading(false);
      }
    };

    loadInitialState();

    return () => {
      isMounted = false;
    };
  }, [applyState]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refresh]);

  useEffect(() => {
    if (!pollingIntervalMs || pollingIntervalMs <= 0) return undefined;
    const intervalId = window.setInterval(() => {
      refresh();
    }, pollingIntervalMs);
    return () => window.clearInterval(intervalId);
  }, [pollingIntervalMs, refresh]);

  useEffect(() => {
    if (updateStatus) {
      const timer = setTimeout(() => {
        setUpdateStatus(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [updateStatus]);

  const updateFlag = useCallback(
    async (flagKey: string, enabled: boolean) => {
      const previousFlags = [...flags];

      setSavingFlags((prev) => new Set(prev).add(flagKey));
      setFlags((prev) =>
        prev.map((f) => (f.key === flagKey ? { ...f, enabled } : f)),
      );

      const result = await updateFeatureFlag({ key: flagKey, enabled });

      setSavingFlags((prev) => {
        const next = new Set(prev);
        next.delete(flagKey);
        return next;
      });

      if (result.success) {
        applyState(result.data);
        setUpdateStatus({ type: 'success', message: '設定を保存しました' });
        setReloadRequired(true);
      } else {
        setFlags(previousFlags);
        setUpdateStatus({ type: 'error', message: result.error });
      }
    },
    [flags, applyState],
  );

  const updateUnlock = useCallback(
    async (enabled: boolean) => {
      if (enabled) {
        const confirmed = window.confirm(
          '全ての機能フラグが有効になります。よろしいですか？',
        );
        if (!confirmed) return;
      }

      const previousUnlock = unlock;

      setIsUnlockSaving(true);
      setUnlock(enabled);

      const result = await updateFeatureFlag({ unlock: enabled });

      setIsUnlockSaving(false);

      if (result.success) {
        applyState(result.data);
        setUpdateStatus({ type: 'success', message: '設定を保存しました' });
        setReloadRequired(true);
      } else {
        setUnlock(previousUnlock);
        setUpdateStatus({ type: 'error', message: result.error });
      }
    },
    [unlock, applyState],
  );

  const isEnabled = useCallback(
    (flagKey: string) => {
      if (unlock) return true;
      const target = flags.find((flag) => flag.key === flagKey);
      return target ? target.enabled : false;
    },
    [flags, unlock],
  );

  // Wire up WorkHistoryHookRegistry defense-in-depth feature flag check
  useEffect(() => {
    // Use optional chaining — setFeatureFlagResolver may not exist in all implementations
    (WorkHistoryHookRegistry as any)?.setFeatureFlagResolver?.(isEnabled);
    return () => {
      (WorkHistoryHookRegistry as any)?.setFeatureFlagResolver?.(undefined);
    };
  }, [isEnabled]);

  const value = useMemo(
    () => ({
      flags,
      unlock,
      pluginHealth,
      isLoading,
      savingFlags,
      isUnlockSaving,
      updateStatus,
      reloadRequired,
      isEnabled,
      refresh,
      updateFlag,
      updateUnlock,
      clearStatus,
    }),
    [
      flags,
      unlock,
      pluginHealth,
      isLoading,
      savingFlags,
      isUnlockSaving,
      updateStatus,
      reloadRequired,
      isEnabled,
      refresh,
      updateFlag,
      updateUnlock,
      clearStatus,
    ],
  );

  if (!pluginsInitialized) {
    return null;
  }

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlagContext = () => useContext(FeatureFlagContext);
